import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Copy, Clock } from 'lucide-react';
import { notify } from '../../components/Notify';
import { getPaymentStatusByIdApi, verifyPaymentApi, cancelPixPaymentApi } from '../../api';

// Tela de espera do Pix: QR + copia-e-cola + contador + polling do status.
export default function PixWaiting({ pix, onAprovado, onCancelar }) {
    const [restante, setRestante] = useState(() => calcRestante(pix?.expires_at));
    const [verificando, setVerificando] = useState(false);
    const timerRef = useRef(null);

    // contador regressivo
    useEffect(() => {
        timerRef.current = setInterval(() => setRestante(calcRestante(pix?.expires_at)), 1000);
        return () => clearInterval(timerRef.current);
    }, [pix?.expires_at]);

    // polling do status (real).
    useEffect(() => {
        if (!pix?.paymentId) return undefined;
        const id = setInterval(async () => {
            try {
                const res = await getPaymentStatusByIdApi({ paymentId: pix.paymentId });
                if (res?.data?.status === 'approved') { clearInterval(id); onAprovado?.(); }
            } catch { /* ignora e tenta de novo */ }
        }, 10000);
        return () => clearInterval(id);
    }, [pix?.paymentId, onAprovado]);

    const copiar = () => {
        navigator.clipboard?.writeText(pix?.pixCopiaECola || '').then(() => notify('Código Pix copiado!'));
    };

    const jaPaguei = async () => {
        setVerificando(true);
        try {
            const res = await verifyPaymentApi({ transactionId: pix.paymentId });
            if (res?.data?.approved) onAprovado?.();
            else notify('Pagamento ainda não identificado. Aguarde alguns instantes.');
        } catch {
            notify('Não foi possível verificar agora.');
        } finally {
            setVerificando(false);
        }
    };

    const cancelar = async () => {
        try {
            const res = await cancelPixPaymentApi({ paymentId: pix.paymentId });
            if (res?.data?.approved) return onAprovado?.();
        } catch { /* ignora */ }
        onCancelar?.();
    };

    return (
        <div className="pcard ueb-pix">
            <h2 className="ueb-pix-title">Pague com Pix</h2>
            <p className="pmuted ueb-pix-sub">Escaneie o QR Code ou copie o código no seu banco.</p>

            <div className="ueb-pix-qr">
                {pix?.pixQrCodeBase64
                    ? <img src={`data:image/jpeg;base64,${pix.pixQrCodeBase64}`} alt="QR Code Pix" width={200} height={200} />
                    : <QRCode value={pix?.pixCopiaECola || 'pix'} size={200} />}
            </div>

            <div className={`ueb-pix-timer ${restante <= 0 ? 'is-exp' : ''}`}>
                <Clock size={15} /> {restante > 0 ? `Expira em ${fmt(restante)}` : 'Pix expirado'}
            </div>

            <label className="ueb-co-label">Pix copia e cola</label>
            <textarea className="ptextarea ueb-pix-code" readOnly rows={3} value={pix?.pixCopiaECola || ''} />
            <button className="pbtn pbtn--soft pbtn--block" onClick={copiar}><Copy size={16} /> Copiar código</button>

            <div className="ueb-pix-actions">
                <button className="pbtn pbtn--ghost" onClick={cancelar}>Cancelar</button>
                <button className="pbtn pbtn--primary" onClick={jaPaguei} disabled={verificando} style={{ flex: 1 }}>
                    {verificando ? <span className="pspin" /> : 'Já paguei'}
                </button>
            </div>
        </div>
    );
}

function calcRestante(expiresAt) {
    if (!expiresAt) return 0;
    const alvo = new Date(expiresAt).getTime();
    if (Number.isNaN(alvo)) return 0;
    return Math.max(0, Math.floor((alvo - Date.now()) / 1000));
}

function fmt(seg) {
    const m = Math.floor(seg / 60);
    const s = seg % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}
